import { Controller } from '@hotwired/stimulus'

// Live thumbnail preview for the product image input. Picked files accumulate
// across multiple selections, so a shopkeeper can add many at once OR keep
// adding more one batch at a time. The running list is synced back into the
// <input> (via DataTransfer) so the form submits every image. Each preview has
// a remove button, and the first image is flagged as the cover ("Main").
export default class extends Controller {
  static targets = ['input', 'preview', 'empty', 'count', 'warnings']

  static values = {
    recommendedWidth: { type: Number, default: 1200 },
    recommendedHeight: { type: Number, default: 1200 }
  }

  connect () {
    this.files = []
    this.urls = []
    this.rejected = []
    this.draw()
  }

  disconnect () {
    this.revoke()
  }

  async add () {
    if (!this.hasInputTarget) return

    this.rejected = []

    for (const file of Array.from(this.inputTarget.files || [])) {
      if (!this.looksLikeImage(file)) {
        this.rejected.push(`${file.name} does not look like an image file`)
        continue
      }

      const duplicate = this.files.some((existing) => (
        existing.name === file.name &&
        existing.size === file.size &&
        existing.lastModified === file.lastModified
      ))
      if (duplicate) continue

      try {
        const dimensions = await this.readDimensions(file)
        if (!dimensions) {
          this.rejected.push(this.unreadableMessage(file))
          continue
        }
      } catch (_error) {
        this.rejected.push(this.unreadableMessage(file))
        continue
      }

      this.files.push(file)
    }

    this.syncInput()
    this.draw()
    this.renderWarnings()
  }

  removeAt (index) {
    this.files.splice(index, 1)
    this.syncInput()
    this.draw()
    this.renderWarnings()
  }

  // Push the running list back into the <input> so all images submit together.
  syncInput () {
    if (!this.hasInputTarget) return

    const transfer = new DataTransfer()
    this.files.forEach((file) => transfer.items.add(file))
    this.inputTarget.files = transfer.files
  }

  async readDimensions (file) {
    const fromHeader = await this.readDimensionsFromHeader(file)
    if (fromHeader) return fromHeader

    return this.readDimensionsWithImage(file)
  }

  looksLikeImage (file) {
    if (file.type && file.type.startsWith('image/')) return true
    const ext = (file.name || '').split('.').pop()?.toLowerCase()
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'heic', 'heif', 'avif', 'svg', 'tif', 'tiff'].includes(ext)
  }

  unreadableMessage (file) {
    const name = (file.name || 'Image').toLowerCase()
    if (name.endsWith('.heic') || name.endsWith('.heif')) {
      return `${file.name} is HEIC. Export or save as JPG/PNG first (iPhone: Share → Save to Files as Most Compatible)`
    }
    return `${file.name} could not be read. The file may be corrupted, renamed, or not a real JPG/PNG. Try re-saving from your gallery or pick another photo.`
  }

  async readDimensionsFromHeader (file) {
    const buffer = await file.slice(0, 512 * 1024).arrayBuffer()
    const bytes = new Uint8Array(buffer)

    if (bytes.length >= 24 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4E && bytes[3] === 0x47) {
      const view = new DataView(buffer)
      return { width: view.getUint32(16), height: view.getUint32(20) }
    }

    if (bytes.length >= 10 && bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
      const view = new DataView(buffer)
      return { width: view.getUint16(6, true), height: view.getUint16(8, true) }
    }

    if (bytes.length >= 30 && bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46 && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50) {
      if (bytes[12] === 0x56 && bytes[13] === 0x50 && bytes[14] === 0x38 && bytes[15] === 0x58) {
        const width = 1 + (bytes[24] | (bytes[25] << 8) | (bytes[26] << 16))
        const height = 1 + (bytes[27] | (bytes[28] << 8) | (bytes[29] << 16))
        return { width, height }
      }
      if (bytes[12] === 0x56 && bytes[13] === 0x50 && bytes[14] === 0x38 && bytes[15] === 0x20 && bytes.length >= 30) {
        const view = new DataView(buffer)
        return { width: view.getUint16(26, true) & 0x3FFF, height: view.getUint16(28, true) & 0x3FFF }
      }
    }

    if (bytes.length >= 2 && bytes[0] === 0xFF && bytes[1] === 0xD8) {
      let offset = 2
      while (offset + 4 < bytes.length) {
        while (offset < bytes.length && bytes[offset] !== 0xFF) offset += 1
        if (offset + 1 >= bytes.length) break

        const marker = bytes[offset + 1]
        if (marker === 0xD8 || marker === 0xD9 || (marker >= 0xD0 && marker <= 0xD7) || marker === 0x01) {
          offset += 2
          continue
        }

        const length = (bytes[offset + 2] << 8) + bytes[offset + 3]
        if (length < 2 || offset + 2 + length > bytes.length) break
        if (marker >= 0xC0 && marker <= 0xCF && marker !== 0xC4 && marker !== 0xC8 && marker !== 0xCC) {
          const height = (bytes[offset + 5] << 8) + bytes[offset + 6]
          const width = (bytes[offset + 7] << 8) + bytes[offset + 8]
          return { width, height }
        }
        offset += 2 + length
      }
    }

    return null
  }

  readDimensionsWithImage (file) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file)
      const img = new Image()
      img.onload = () => {
        URL.revokeObjectURL(url)
        resolve({ width: img.naturalWidth, height: img.naturalHeight })
      }
      img.onerror = () => {
        URL.revokeObjectURL(url)
        reject(new Error('unreadable'))
      }
      img.src = url
    })
  }

  renderWarnings () {
    if (!this.hasWarningsTarget) return

    if (this.rejected.length === 0) {
      this.warningsTarget.classList.add('hidden')
      this.warningsTarget.innerHTML = ''
      return
    }

    this.warningsTarget.classList.remove('hidden')
    this.warningsTarget.innerHTML = [
      '<p class="font-semibold">Some images were skipped:</p>',
      '<ul class="mt-1.5 list-inside list-disc space-y-0.5">',
      ...this.rejected.map((message) => `<li>${this.escapeHtml(message)}</li>`),
      '</ul>'
    ].join('')
  }

  escapeHtml (value) {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  revoke () {
    this.urls.forEach((url) => URL.revokeObjectURL(url))
    this.urls = []
  }

  draw () {
    if (!this.hasPreviewTarget) return

    this.revoke()
    this.previewTarget.innerHTML = ''

    const count = this.files.length
    if (this.hasEmptyTarget) this.emptyTarget.classList.toggle('hidden', count > 0)
    if (this.hasCountTarget) {
      this.countTarget.textContent = count
        ? `${count} new image${count > 1 ? 's' : ''} ready`
        : ''
    }

    this.files.forEach((file, index) => {
      const url = URL.createObjectURL(file)
      this.urls.push(url)

      const figure = document.createElement('figure')
      figure.className = 'group relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50 ring-1 ring-slate-200/70'

      const img = document.createElement('img')
      img.src = url
      img.alt = file.name
      img.className = 'aspect-square w-full object-cover'
      figure.appendChild(img)

      if (index === 0) {
        const badge = document.createElement('span')
        badge.textContent = 'Main'
        badge.className = 'absolute left-1.5 top-1.5 rounded-md bg-brand-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white shadow'
        figure.appendChild(badge)
      }

      const remove = document.createElement('button')
      remove.type = 'button'
      remove.setAttribute('aria-label', `Remove ${file.name}`)
      remove.className = 'absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-slate-900/70 text-white opacity-0 transition hover:bg-rose-600 focus:opacity-100 group-hover:opacity-100'
      remove.innerHTML = '<svg class="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M6 18L18 6M6 6l12 12"/></svg>'
      remove.addEventListener('click', () => this.removeAt(index))
      figure.appendChild(remove)

      this.previewTarget.appendChild(figure)
    })
  }
}
